import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjB0ZWNoJTIwb2ZmaWNlfGVufDB8fHx8MTc4Njk2ODAxMHww&ixlib=rb-4.1.0&q=85";

function Shell({ children, heading, sub }) {
  return (
    <div className="grid min-h-screen bg-slate-50 lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden border-r border-slate-200 lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.18]" />
        <div className="absolute inset-0 bg-gradient-to-tr from-white via-slate-50/90 to-blue-50/70" />
        <div className="grid-lines absolute inset-0 opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded bg-blue-600 text-sm font-semibold text-white">D</span>
            <span>
              <span className="block text-sm font-semibold tracking-[0.2em] text-slate-900">DIGNIFYD × IMZ</span>
              <span className="block text-[10px] tracking-[0.22em] text-slate-500">TALENT CONNECT</span>
            </span>
          </div>
          <div className="max-w-lg">
            <p className="label-xs mb-4">Talent intelligence platform</p>
            <h2 className="text-3xl font-light leading-snug tracking-tight text-slate-900">
              Connecting Exceptional Talent with Exceptional Opportunities.
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-500">
              Purpose-built for the DIGNIFYD × IMZ engagement — resume intelligence, transparent
              candidate matching, a guided hiring workflow and a client portal for IMZ hiring managers.
            </p>
            <div className="mt-9 grid grid-cols-3 gap-6 border-t border-slate-200 pt-6">
              {[
                ["Talent records", "64+"],
                ["Live requirements", "15"],
                ["Client accounts", "12"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-mono-num text-xl text-slate-900">{v}</p>
                  <p className="label-xs mt-1">{k}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="flex items-center gap-2 text-[11px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Role-based access with client-level data isolation
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-14 sm:px-12">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="grid h-9 w-9 place-items-center rounded bg-blue-600 text-sm font-semibold text-white">D</span>
            <span className="text-sm font-semibold tracking-[0.18em] text-slate-900">DIGNIFYD</span>
          </div>
          <p className="label-xs mb-2 text-[11px] tracking-[0.2em] text-blue-600">DIGNIFYD × IMZ</p>
          <p className="label-xs mb-3">{sub}</p>
          <h1 className="text-2xl font-medium tracking-tight text-slate-900">{heading}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "h-10 border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-600";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    toast.success(`Welcome back, ${res.user.name.split(" ")[0]}`);
    navigate("/dashboard");
  };

  return (
    <Shell heading="Sign in to your workspace" sub="Recruiter access">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label className="label-xs">Work email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className={inputCls} autoComplete="email" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="label-xs">Password</Label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[11px] text-blue-600 hover:text-blue-700">
              Forgot password?
            </Link>
          </div>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className={inputCls} autoComplete="current-password" />
        </div>
        {error && (
          <p data-testid="login-error" className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="h-10 w-full gap-2 rounded-full bg-blue-600 text-sm hover:bg-blue-500">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Sign in
        </Button>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell heading="Reset your password" sub="Account recovery">
      {sent ? (
        <div className="mt-8 space-y-5" data-testid="forgot-success">
          <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-700">
            If that email is registered, a reset link is on its way. The link stays valid for one hour.
          </p>
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700">
            Back to sign in <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-1.5">
            <Label className="label-xs">Work email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className={inputCls} />
          </div>
          {error && <p className="text-xs text-rose-700">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="h-10 w-full rounded-full bg-blue-600 hover:bg-blue-500">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
          </Button>
          <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-slate-600">
            Back to sign in
          </Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell heading="Choose a new password" sub="Account recovery">
      <form onSubmit={submit} className="mt-8 space-y-4">
        {!token && (
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This reset link is missing its token. Request a new link from the forgot password page.
          </p>
        )}
        <div className="space-y-1.5">
          <Label className="label-xs">New password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label className="label-xs">Confirm password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" className={inputCls} />
        </div>
        {error && <p data-testid="reset-error" className="text-xs text-rose-700">{error}</p>}
        <Button type="submit" disabled={busy || !token} data-testid="reset-submit-button" className="h-10 w-full rounded-full bg-blue-600 hover:bg-blue-500">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </Button>
      </form>
    </Shell>
  );
}
